/**
 * The wire between the butler (`src/butler/run.ts`, the Worker) and the
 * screen (`app/src/screens/Butler.tsx`). Lives on the app side and is
 * imported by the Worker, the same direction `src/tickets.ts` imports
 * `./ticketRules.ts`: no DOM, no runtime imports beyond a type.
 *
 * `docs/butler-plan/README.md`'s *The shape* and *The contract*, and
 * `docs/butler-plan/wp-9.0-schema-and-contract.md` §2, are the
 * specification. 9.1 (the loop) and 9.2 (the screen) build against this
 * file from opposite ends — frozen here so neither has to guess the other's
 * half (the WP 2.4b lesson). Version 3 (WP 9.11,
 * `docs/butler-plan/wp-9.11-a-classic-chat-worker.md`) adds the same way:
 * a `thinking` event, a `trail` part and two proposal kinds (`ask`,
 * `followups`), pushed first so 9.12 (the screen half) builds against them.
 */
import type { ListKind } from './types'

// ---------------------------------------------------------------------------
// The wire
// ---------------------------------------------------------------------------

/** One SSE frame's payload. `text` deltas arrive one word or clause at a
 *  time; everything else arrives whole. */
export type ChatEvent =
  | { type: 'text'; delta: string }
  // WP 9.11 — the model's own summarised reasoning, streamed the same way
  // `text` is (`thinking: { type: 'adaptive', display: 'summarized' }` on
  // the request). Shown live, in its own collapsible trail; never
  // persisted and never resent as history (`partsFromEvents` drops it,
  // `history.ts` rebuilds from parts, which never had it).
  | { type: 'thinking'; delta: string }
  | { type: 'status'; label: string }
  | { type: 'cards'; entry_ids: string[] }
  | { type: 'proposal'; id: string; action: Proposal }
  | { type: 'sources'; items: { url: string; title: string }[] }
  | { type: 'usage'; cost_usd: number; calls: number; searches: number; fetches: number }
  | { type: 'error'; message: string; retryable: boolean }
  | { type: 'done'; message_id: string }

/** A write the butler proposes rather than performs — "Reads run on the
 *  Worker; writes run on the phone" (README, *The shape*). 9.4 maps each
 *  kind to the same mutation hook every other screen uses. */
export type Proposal =
  | { kind: 'save'; entry_id: string }
  | { kind: 'schedule'; entry_id: string; starts_at: string | null }
  | { kind: 'rate'; entry_id: string; stars: 1 | 2 | 3 | 4 | 5 | null }
  | {
      kind: 'add_item'
      list_kind: ListKind
      list_id: string | null
      body: string
      link?: string
      price_cents?: number
      currency?: string
      image_url?: string
      qty?: string
    }
  | { kind: 'note'; note_kind: 'fix' | 'change' | 'idea'; body: string; entry_id?: string; venue_id?: string }
  | { kind: 'remember'; body: string; expires_on?: string }
  // Places is a mode of Browse (WP 6.3), not a screen of its own —
  // `browse.ts`'s `browseModeFrom` reads `query.mode === 'places'`, so a
  // places proposal is `{ screen: 'browse', query: { mode: 'places' } }`,
  // never a `'places'` screen name.
  | { kind: 'open'; screen: 'browse' | 'year' | 'lists' | 'city'; id?: string; query?: Record<string, string> }
  // WP 9.9 — never a tool call (the manifest is untouched; this proposal is
  // `run.ts`'s own deterministic over-cap branch, not the model's
  // decision). `retry_text` carries the question that was answered
  // database-only, so a tap can ask it again with the grant in place.
  | { kind: 'spend_more'; amount_usd: number; day: string; retry_text: string }
  // WP 9.11 — `ask_doug`, a phone tool: the butler asks when, and only
  // when, the answer changes what it would do next and the rows in hand
  // cannot tell it. Doug's tap (or his own free text) arrives as his next
  // message; `history.ts` reads it back as `[Doug answered: "…"]`.
  | { kind: 'ask'; question: string; options: string[]; allow_free_text?: boolean }
  // WP 9.11 — `offer_followups`, called after the turn's own answer, at
  // the end of a turn that answered something: what Doug would plausibly
  // ask next about these results, in his words.
  | { kind: 'followups'; questions: string[] }

export interface ChatRequest {
  /** `null`: the Worker starts (or reuses) today's thread. */
  thread_id: string | null
  text: string
  outcomes: { proposal_id: string; result: 'done' | 'declined' }[]
}

/** A persisted message's `parts`: what the screen draws, never raw API
 *  content (`chat_messages.parts`). API history is rebuilt from this each
 *  turn — "one value, one job" (README, *Traps this build will meet*). */
export type MessagePart =
  | { type: 'text'; text: string }
  // `titles` and `firsts` are written by the Worker when it persists the
  // turn (`run.ts`'s `finish`), never by an event: a live part has neither.
  // a2-05 — they let a card whose event has left the feed still draw its
  // title and day (`firsts`: id -> Berlin `YYYY-MM-DD`). A row persisted
  // before a2-05 has titles and no `firsts`; one before WP 9.7 has neither.
  | { type: 'cards'; entry_ids: string[]; titles?: Record<string, string>; firsts?: Record<string, string> }
  | { type: 'proposal'; id: string; action: Proposal; outcome: 'offered' | 'done' | 'declined' }
  | { type: 'sources'; items: { url: string; title: string }[] }
  | { type: 'error'; message: string }
  // WP 9.11 — persisted once, at the end of a turn, from `persist.ts`'s
  // `trailPart` — never a `ChatEvent` (the live line stays `status`; this
  // is only for a past turn reopened later). One step per status label,
  // in order; `detail` is the card count for a find step or the source
  // hosts for a search step, whichever detail-bearing event landed on it
  // first (see `trailPart`'s own comment).
  | { type: 'trail'; steps: { label: string; detail?: string }[] }

// WP 9.11 — three new shapes: the `thinking` event, the `trail` part, and
// the `ask`/`followups` proposals.
export const CHAT_PROTOCOL_VERSION = 3

// ---------------------------------------------------------------------------
// Encoding — one SSE frame per event
// ---------------------------------------------------------------------------

export function encodeEvent(e: ChatEvent): string {
  return `data: ${JSON.stringify(e)}\n\n`
}

// ---------------------------------------------------------------------------
// Decoding — chunk-tolerant, no I/O
// ---------------------------------------------------------------------------

/** Reassembles SSE frames from arbitrarily-split chunks (a `fetch` body
 *  reader never promises a chunk boundary lines up with a frame's `\n\n`,
 *  and can split mid-JSON too). `push` returns whatever frames a chunk
 *  completed; `flush` returns a trailing frame the stream closed without a
 *  terminator on — the error case 9.2's *interrupted turn* relies on. */
export class SseDecoder {
  private buffer = ''

  push(chunk: string): ChatEvent[] {
    this.buffer += chunk
    return this.drain(false)
  }

  flush(): ChatEvent[] {
    const events = this.drain(true)
    this.buffer = ''
    return events
  }

  private drain(final: boolean): ChatEvent[] {
    const events: ChatEvent[] = []
    for (;;) {
      const sep = this.buffer.indexOf('\n\n')
      if (sep === -1) {
        if (final) {
          const event = this.decodeFrame(this.buffer)
          if (event) events.push(event)
        }
        break
      }
      const frame = this.buffer.slice(0, sep)
      this.buffer = this.buffer.slice(sep + 2)
      const event = this.decodeFrame(frame)
      if (event) events.push(event)
    }
    return events
  }

  private decodeFrame(frame: string): ChatEvent | null {
    const dataLines = frame
      .split('\n')
      .filter(line => line.startsWith('data:'))
      .map(line => line.slice(5).replace(/^ /, ''))
    if (dataLines.length === 0) return null

    let parsed: unknown
    try {
      parsed = JSON.parse(dataLines.join('\n'))
    } catch {
      return null
    }
    return isChatEvent(parsed) ? parsed : null
  }
}

// ---------------------------------------------------------------------------
// Shape checks — a decoder that throws on a malformed frame turns a stray
// byte on the wire into a dead thread; every check here answers false
// instead.
// ---------------------------------------------------------------------------

const isString = (x: unknown): x is string => typeof x === 'string'
const isNumber = (x: unknown): x is number => typeof x === 'number'
const isBoolean = (x: unknown): x is boolean => typeof x === 'boolean'
const isObject = (x: unknown): x is Record<string, unknown> =>
  typeof x === 'object' && x !== null && !Array.isArray(x)

function isSourceItem(x: unknown): x is { url: string; title: string } {
  return isObject(x) && isString(x.url) && isString(x.title)
}

const PROPOSAL_KINDS = [
  'save', 'schedule', 'rate', 'add_item', 'note', 'remember', 'open', 'spend_more', 'ask', 'followups',
] as const

/** A shape check, not a schema validator (the manifest's zod schemas own
 *  that job for tool input) — enough to tell a real `Proposal` from garbage
 *  without importing zod into app code the Worker also loads. */
export function isProposal(x: unknown): x is Proposal {
  if (!isObject(x) || !isString(x.kind)) return false
  if (!(PROPOSAL_KINDS as readonly string[]).includes(x.kind)) return false

  switch (x.kind) {
    case 'save':
      return isString(x.entry_id)
    case 'schedule':
      return isString(x.entry_id) && (x.starts_at === null || isString(x.starts_at))
    case 'rate':
      return isString(x.entry_id) && (x.stars === null || (isNumber(x.stars) && x.stars >= 1 && x.stars <= 5))
    case 'add_item':
      return isString(x.list_kind) && (x.list_id === null || isString(x.list_id)) && isString(x.body)
    case 'note':
      return isString(x.note_kind) && isString(x.body)
    case 'remember':
      return isString(x.body)
    case 'open':
      return isString(x.screen)
    case 'spend_more':
      return isNumber(x.amount_usd) && isString(x.day) && isString(x.retry_text)
    case 'ask':
      return (
        isString(x.question)
        && Array.isArray(x.options) && x.options.length >= 2 && x.options.length <= 4 && x.options.every(isString)
        && (x.allow_free_text === undefined || isBoolean(x.allow_free_text))
      )
    case 'followups':
      return Array.isArray(x.questions) && x.questions.length >= 1 && x.questions.length <= 3 && x.questions.every(isString)
    default:
      return false
  }
}

/** `isChatEvent(x)` — used by `SseDecoder` so an unknown `type` (an older
 *  client on the wire during a deploy, say) is dropped rather than thrown.
 *  Exported so a test can assert the drop directly. */
export function isChatEvent(x: unknown): x is ChatEvent {
  if (!isObject(x) || !isString(x.type)) return false

  switch (x.type) {
    case 'text':
      return isString(x.delta)
    case 'thinking':
      return isString(x.delta)
    case 'status':
      return isString(x.label)
    case 'cards':
      return Array.isArray(x.entry_ids) && x.entry_ids.every(isString)
    case 'proposal':
      return isString(x.id) && isProposal(x.action)
    case 'sources':
      return Array.isArray(x.items) && x.items.every(isSourceItem)
    case 'usage':
      return isNumber(x.cost_usd) && isNumber(x.calls) && isNumber(x.searches) && isNumber(x.fetches)
    case 'error':
      return isString(x.message) && isBoolean(x.retryable)
    case 'done':
      return isString(x.message_id)
    default:
      return false
  }
}

// ---------------------------------------------------------------------------
// parts — what a finished (or in-progress) turn persists and the screen draws
// ---------------------------------------------------------------------------

/** Folds a turn's events into the `MessagePart[]` the screen renders and
 *  `chat_messages.parts` persists: consecutive `text` deltas join into one
 *  part, a `proposal` starts `offered` (9.4's chip taps move it to `done` or
 *  `declined` after the fact), `status`/`usage`/`done` carry nothing worth
 *  keeping once the turn is over. */
export function partsFromEvents(events: ChatEvent[]): MessagePart[] {
  const parts: MessagePart[] = []
  let textBuf = ''

  const flushText = () => {
    if (textBuf) {
      parts.push({ type: 'text', text: textBuf })
      textBuf = ''
    }
  }

  for (const e of events) {
    switch (e.type) {
      case 'text':
        textBuf += e.delta
        break
      case 'cards':
        flushText()
        parts.push({ type: 'cards', entry_ids: e.entry_ids })
        break
      case 'proposal':
        flushText()
        parts.push({ type: 'proposal', id: e.id, action: e.action, outcome: 'offered' })
        break
      case 'sources':
        flushText()
        parts.push({ type: 'sources', items: e.items })
        break
      case 'error':
        flushText()
        parts.push({ type: 'error', message: e.message })
        break
      case 'thinking':
      case 'status':
      case 'usage':
      case 'done':
        break
    }
  }
  flushText()

  return parts
}
